import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { trpcClient } from '../../utils/trpc'

/**
 * @fileoverview Admin Sidebar Store
 *
 * Zustand store for managing admin sidebar state.
 * Fetches menu items from the backend based on organization context.
 */

interface MenuItem {
	id: string
	label: string
	href: string
	badge?: number
}

interface SidebarState {
	menuItems: MenuItem[]
	bottomActions: MenuItem[]
	isLoading: boolean
	error: string | null
}

interface SidebarActions {
	initialize: (organizationId?: string) => Promise<void>
	clearError: () => void
	reset: () => void
}

type SidebarStore = SidebarState & SidebarActions

export const useAdminSidebarStore = create<SidebarStore>()(
	devtools(
		(set) => ({
			// Initial state
			menuItems: [],
			bottomActions: [],
			isLoading: false,
			error: null,

			// Actions
			initialize: async (organizationId?: string) => {
				set({ isLoading: true, error: null })

				try {
					const data = await trpcClient.admin.sidebar.initialize.query({
						organizationId,
					})

					set({
						menuItems: data.menuItems,
						bottomActions: data.bottomActions,
						isLoading: false,
					})
				} catch (error) {
					console.error('Failed to initialize sidebar:', error)
					set({
						error: error instanceof Error ? error.message : 'Failed to initialize sidebar',
						isLoading: false,
					})
				}
			},

			clearError: () => set({ error: null }),

			reset: () =>
				set({
					menuItems: [],
					bottomActions: [],
					isLoading: false,
					error: null,
				}),
		}),
		{ name: 'admin-sidebar-store' },
	),
)
